"use strict";
/*
 * IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: IBM Cognos Analytics
 *
 * (C) Copyright IBM Corp. 2021
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const react_dom_1 = require("react-dom");
const carbon_components_react_1 = require("carbon-components-react");
const utils_1 = require("../utils");
const COMPONENT_NAME = 'ComboBox';
const DISPLAY_NAME = (0, utils_1.getDisplayName)(COMPONENT_NAME);
const DEFAULT_TID = (0, utils_1.getDefaultTid)(COMPONENT_NAME);
// The whole class has been created to address Carbon issue...
// https://github.com/carbon-design-system/carbon/issues/8170
class ForwardRefComboBox extends react_1.Component {
    constructor() {
        super(...arguments);
        this.state = {
            // currentSelectedItem holds the current valid selectedItem
            currentSelectedItem: null,
        };
        this.updateCurrentSelectedItem = selectedItem => {
            let foundItem = null;
            if (selectedItem !== undefined && selectedItem !== null) {
                const { items } = this.props;
                const labelToFind = this.handleItemToString(selectedItem).toLowerCase();
                foundItem =
                    items.find(item => this.handleItemToString(item).toLowerCase() === labelToFind) ||
                        null;
            }
            this.setState({ currentSelectedItem: foundItem });
        };
        // bucLocalInputTag holds a reference to the ComboBox'es input tag
        this.bucLocalInputTag = undefined;
        this.handleKeyUp = ({ keyCode }) => {
            const { items } = this.props;
            // check if the key pressed was ENTER
            if (keyCode === 13) {
                // retrieve the current value
                const typedInLabel = this.bucLocalInputTag.value;
                const typedInLabelLowerCase = typedInLabel.toLowerCase();
                // check if the input matches an item or is invalid
                // special check if empty = no match
                const foundItem = typedInLabelLowerCase &&
                    items.find(item => this.handleItemToString(item)
                        .toLowerCase()
                        .startsWith(typedInLabelLowerCase));
                // if invalid (does not match an item)
                // reset the input value to the last current Valid label
                if (!foundItem) {
                    const saveState = this.state.currentSelectedItem;
                    // need to TOGGLE the selectedItem in order to get Carbon to re-render and FIX the input.value
                    // making an assumption that there are at least two items in the drop down list
                    this.setState({
                        // setting this will call onChange - that is why we need saveState
                        currentSelectedItem: items[0] === saveState ? items[1] : items[0],
                    });
                    this.setState({
                        currentSelectedItem: typedInLabelLowerCase ? saveState : null,
                    });
                }
            }
        };
        // Needed to handle invalid typed in entries
        // If the typed in value does not match an entry in items, item will be null
        this.handleItemToString = item => {
            var _a, _b, _c, _d;
            if (!item || typeof item === 'string') {
                return item !== null && item !== void 0 ? item : '';
            }
            return (_d = (_c = (_b = (_a = this.props).itemToString) === null || _b === void 0 ? void 0 : _b.call(_a, item)) !== null && _c !== void 0 ? _c : item.label) !== null && _d !== void 0 ? _d : '';
        };
        this.handleOnChange = onChangeObject => {
            var _a, _b;
            // update currentSelectedItem to track the new selectedItem
            this.setState({ currentSelectedItem: onChangeObject.selectedItem });
            (_b = (_a = this.props).onChange) === null || _b === void 0 ? void 0 : _b.call(_a, onChangeObject);
        };
        this.handleRef = refObject => {
            var _a, _b, _c, _d;
            if (refObject) {
                const domNode = (0, react_dom_1.findDOMNode)(refObject);
                // This should be done via downshiftProps.getInputProps but there's a Carbon bug.
                // The downshift stateReducer(Downshift.stateChangeTypes.keyDownEnter) can not be
                // used to capture ENTER as downshifts internal state is not actually changing.
                this.bucLocalInputTag = domNode.querySelector('input');
                // DO NOT USE "keydown", Carbon has used this and does not propagate the event.
                (_b = (_a = this.bucLocalInputTag) === null || _a === void 0 ? void 0 : _a.addEventListener) === null || _b === void 0 ? void 0 : _b.call(_a, 'keyup', this.handleKeyUp);
            }
            (_d = (_c = this.props).innerRef) === null || _d === void 0 ? void 0 : _d.call(_c, refObject);
        };
    }
    componentDidMount() {
        const { initialSelectedItem, selectedItem } = this.props;
        // we should also be using initialInputValue and InputValue, but Carbon misuses them
        this.updateCurrentSelectedItem(initialSelectedItem !== null && initialSelectedItem !== void 0 ? initialSelectedItem : selectedItem);
    }
    componentDidUpdate(prevProps) {
        // if client updates the selectedItem, reflect the change
        if (this.handleItemToString(prevProps.selectedItem) !==
            this.handleItemToString(this.props.selectedItem)) {
            this.updateCurrentSelectedItem(this.props.selectedItem);
        }
    }
    render() {
        const _a = this.props, { 
        // remove - remapped and local props
        'data-tid': dataTid, id, itemToString, // eslint-disable-line @typescript-eslint/no-unused-vars
        onChange, // eslint-disable-line @typescript-eslint/no-unused-vars
        innerRef, // eslint-disable-line @typescript-eslint/no-unused-vars
        selectedItem } = _a, // eslint-disable-line @typescript-eslint/no-unused-vars
        // pass all other props to Carbon
        otherCarbonProps = tslib_1.__rest(_a, ['data-tid', "id", "itemToString", "onChange", "innerRef", "selectedItem"]);
        const mappedProps = {
            'data-tid': dataTid !== null && dataTid !== void 0 ? dataTid : DEFAULT_TID,
            id: id !== null && id !== void 0 ? id : (0, utils_1.getUniqueId)(DISPLAY_NAME),
            itemToString: this.handleItemToString,
            onChange: this.handleOnChange,
            ref: this.handleRef,
            selectedItem: this.state.currentSelectedItem,
        };
        return react_1.default.createElement(carbon_components_react_1.ComboBox, Object.assign({}, otherCarbonProps, mappedProps));
    }
}
const ComboBox = react_1.default.forwardRef((props, ref) => {
    const otherProps = ref ? { innerRef: ref } : {};
    return react_1.default.createElement(ForwardRefComboBox, Object.assign({}, props, otherProps));
});
ComboBox.displayName = DISPLAY_NAME;
exports.default = ComboBox;
